import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { 
  Mail, 
  Phone, 
  MapPin, 
  Shield, 
  Clock, 
  AlertTriangle,
  Send,
  CheckCircle,
  XCircle
} from 'lucide-react';
import { webhookConfig, getHeaders } from '../config/webhook';

const ContactPage = () => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1
  });

  const [formData, setFormData] = useState({
    name: '',
    organization: '',
    email: '',
    phone: '',
    service: '',
    message: '',
    clearanceLevel: '',
    urgency: 'standard'
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null); // 'success', 'error', null

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      const response = await fetch(webhookConfig.url, {
        method: 'POST',
        headers: getHeaders(),
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        setSubmitStatus('success');
        // Reset form on success
        setFormData({
          name: '',
          organization: '',
          email: '',
          phone: '',
          service: '',
          message: '',
          clearanceLevel: '',
          urgency: 'standard'
        });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const contactInfo = [
    {
      icon: Phone,
      title: "Emergency Hotline",
      details: "+91 7506665502 (India)",
      description: "24/7 Emergency Response"
    },
    {
      icon: Mail,
      title: "Secure Communications",
      details: "connect@ciarl.com",
      description: "Encrypted Email Available"
    },
    {
      icon: MapPin,
      title: "Operations Center",
      details: "Classified Location",
      description: "By Appointment Only"
    },
    {
      icon: Clock,
      title: "Response Time",
      details: "< 1 Hour",
      description: "Critical Incidents"
    }
  ];

  const services = [
    "Cyber Security Assessment",
    "Digital Forensics Investigation",
    "Counter-Intelligence Operations",
    "Surveillance Systems",
    "Data Recovery Services",
    "Drone Operations",
    "Emergency Response",
    "Consultation Services"
  ];

  const clearanceLevels = [
    "Public/Commercial",
    "Confidential",
    "Secret",
    "Top Secret",
    "SCI (Sensitive Compartmented Information)"
  ];

  return (
    <section className="section-padding bg-gray-dark relative overflow-hidden" ref={ref}>
      {/* Background Pattern */}
      <div className="absolute inset-0 grid-pattern opacity-20" />
      
      <div className="container relative z-10">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
          className="text-center mt-16 mb-16"
        >
          <h2 className="heading-font text-5xl md:text-6xl font-bold mb-6">
            Contact <span className="text-royal-gold">CIARL</span>
          </h2>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto">
            Ready to enhance your security posture? Contact our team of experts for 
            confidential consultation and rapid response services.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
          {/* Contact Information */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            animate={inView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="lg:col-span-1"
          >
            <div className="space-y-6">
              {/* <div className="glass-morphism p-6 rounded-24">
                <div className="flex items-center space-x-3 mb-4">
                  <AlertTriangle className="w-6 h-6 text-royal-gold" />
                  <h3 className="text-xl font-semibold text-off-white">Emergency Contact</h3>
                </div>
                <p className="text-gray-300 text-sm leading-relaxed">
                  For immediate security incidents, cyber attacks, or emergency situations, 
                  use our 24/7 emergency hotline for rapid response.
                </p>
              </div> */}

              {contactInfo.map((info, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 30 }}
                  animate={inView ? { opacity: 1, y: 0 } : {}}
                  transition={{ duration: 0.6, delay: 0.3 + index * 0.1 }}
                  className="glass-morphism p-6 rounded-16 hover:border-royal-gold transition-all duration-300 group"
                >
                  <div className="flex items-start space-x-4">
                    <div className="w-12 h-12 bg-gradient-to-br from-royal-gold to-gold-light rounded-12 flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform duration-300">
                      <info.icon className="w-6 h-6 text-near-black" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-off-white mb-1 group-hover:text-royal-gold transition-colors duration-300">
                        {info.title}
                      </h4>
                      <p className="text-royal-gold font-medium mb-1">
                        {info.details}
                      </p>
                      <p className="text-gray-300 text-sm">
                        {info.description}
                      </p>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
        </motion.div>

          {/* Contact Form */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            animate={inView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.8, delay: 0.4 }}
            className="lg:col-span-2 mb-16"
          >
            <div className="glass-morphism p-8 rounded-24">
              <div className="flex items-center space-x-3 mb-6">
                <Shield className="w-6 h-6 text-royal-gold" />
                <h3 className="text-2xl font-semibold text-off-white">Secure Request Form</h3>
              </div>

              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-gray-300 text-sm font-medium mb-2">
                      Full Name *
                    </label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      className="w-full px-4 py-3 bg-near-black border border-gray-600 rounded-12 text-off-white focus:border-royal-gold focus:outline-none transition-colors duration-300"
                      required
                    />
                  </div>
                  
                  <div>
                    <label className="block text-gray-300 text-sm font-medium mb-2">
                      Organization *
                    </label>
                    <input
                      type="text"
                      name="organization"
                      value={formData.organization}
                      onChange={handleChange}
                      className="w-full px-4 py-3 bg-near-black border border-gray-600 rounded-12 text-off-white focus:border-royal-gold focus:outline-none transition-colors duration-300"
                      required
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-gray-300 text-sm font-medium mb-2">
                      Email Address *
                    </label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      className="w-full px-4 py-3 bg-near-black border border-gray-600 rounded-12 text-off-white focus:border-royal-gold focus:outline-none transition-colors duration-300"
                      required
                    />
                  </div>
                  
                  <div>
                    <label className="block text-gray-300 text-sm font-medium mb-2">
                      Phone Number
                    </label>
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      className="w-full px-4 py-3 bg-near-black border border-gray-600 rounded-12 text-off-white focus:border-royal-gold focus:outline-none transition-colors duration-300"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-gray-300 text-sm font-medium mb-2">
                      Service Required
                    </label>
                    <select
                      name="service"
                      value={formData.service}
                      onChange={handleChange}
                      className="w-full px-4 py-3 bg-near-black border border-gray-600 rounded-12 text-off-white focus:border-royal-gold focus:outline-none transition-colors duration-300"
                    >
                      <option value="">Select Service</option>
                      {services.map((service, index) => (
                        <option key={index} value={service}>{service}</option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-gray-300 text-sm font-medium mb-2">
                      Clearance Level
                    </label>
                    <select
                      name="clearanceLevel"
                      value={formData.clearanceLevel}
                      onChange={handleChange}
                      className="w-full px-4 py-3 bg-near-black border border-gray-600 rounded-12 text-off-white focus:border-royal-gold focus:outline-none transition-colors duration-300"
                    >
                      <option value="">Select Clearance</option>
                      {clearanceLevels.map((level, index) => (
                        <option key={index} value={level}>{level}</option>
                      ))}
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-gray-300 text-sm font-medium mb-2">
                    Request Urgency
                  </label>
                  <div className="flex space-x-4">
                    {['standard', 'urgent', 'emergency'].map((urgency) => (
                      <label key={urgency} className="flex items-center space-x-2">
                        <input
                          type="radio"
                          name="urgency"
                          value={urgency}
                          checked={formData.urgency === urgency}
                          onChange={handleChange}
                          className="text-royal-gold focus:ring-royal-gold"
                        />
                        <span className="text-gray-300 capitalize">{urgency}</span>
                      </label>
                    ))}
                  </div>
                </div>

                <div>
                  <label className="block text-gray-300 text-sm font-medium mb-2">
                    Message *
                  </label>
                  <textarea
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    rows={4}
                    className="w-full px-4 py-3 bg-near-black border border-gray-600 rounded-12 text-off-white focus:border-royal-gold focus:outline-none transition-colors duration-300 resize-none"
                    placeholder="Please provide details about your security requirements..."
                    required
                  />
                </div>

                <div className="flex items-center justify-between pt-4">
                  <p className="text-gray-400 text-sm">
                    * All communications are encrypted and secure
                  </p>
                  
                  <div className="flex items-center space-x-4">
                    {submitStatus === 'success' && (
                      <div className="flex items-center space-x-2 text-green-400">
                        <CheckCircle className="w-4 h-4" />
                        <span className="text-sm">Message sent successfully!</span>
                      </div>
                    )}
                    
                    {submitStatus === 'error' && (
                      <div className="flex items-center space-x-2 text-red-400">
                        <XCircle className="w-4 h-4" />
                        <span className="text-sm">Failed to send message. Please try again.</span>
                      </div>
                    )}
                  
                  <button
                    type="submit"
                      disabled={isSubmitting}
                      className={`cta-button flex items-center space-x-2 ${
                        isSubmitting ? 'opacity-50 cursor-not-allowed' : ''
                      }`}
                    >
                      {isSubmitting ? (
                        <>
                          <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                          <span>Sending...</span>
                        </>
                      ) : (
                        <>
                    <Send className="w-4 h-4" />
                    <span>Send Secure Message</span>
                        </>
                      )}
                  </button>
                  </div>
                </div>
              </form>
            </div>
          </motion.div>
        </div>
      </div>

      {/* Address and Map Row - moved below main content, above Footer */}
      <div className="container relative z-10 mt-16 mb-8">
        <div className="flex flex-col md:flex-row gap-8">
          <div className="glass-morphism p-6 rounded-16 flex-1 flex items-start">
            <MapPin className="w-6 h-6 text-royal-gold flex-shrink-0 mr-4 mt-1" />
            <div>
              <h4 className="font-semibold text-off-white mb-1">Office Address</h4>
              <p className="text-gray-300 text-sm">
                5th floor, Varad NX, above VIP Showroom, near Naupada Police Station, M.G.Road, Thane West - 400602
              </p>
            </div>
          </div>
          <div className="glass-morphism p-2 rounded-16 flex-1 flex items-center justify-center min-h-[350px]">
            <div className="w-full h-[350px] rounded-16 overflow-hidden border border-gray-700 shadow-lg">
              <iframe
                title="CIARL Office Location"
                src="https://www.google.com/maps?q=19.189456258027246,72.96748329708777&z=18&output=embed"
                width="100%"
                height="350"
                style={{ border: 0 }}
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              ></iframe>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ContactPage;
